import { Form } from '@/common/form/Form';
import { useForm } from '@/common/form/FormContext';
import { useFormField } from '@/common/form/hooks/useFormField';
import { fas } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from '@mui/material';
import { useRef, useState } from 'react';
import { StringUtils } from '../../../utils/StringUtils';
import { TreeUtils } from '../../../utils/TreeUtils';
import { DimTree } from './DimTree';
import { KpiSelect } from './KpiSelect';

// Trend layouts: how matched KPI leaves expand against the trended dim values.
const LAYOUTS = [
  { value: 'KPI_OVER_DIM', label: 'KPI / Dimension' },
  { value: 'DIM_OVER_KPI', label: 'Dimension / KPI' },
];

// Max number of value columns a trend may project. The count multiplies across trended dims, so a
// wide baseline or many KPIs can explode the grid; apply is blocked past this.
const COLUMN_LIMIT = 100;

// Period dim tree levels (from PeriodSvc.tree): Year=1, Quarter=2, Month=3. A "group by" shortcut
// buckets the baseline leaves under the tree nodes at the chosen periodicity's level.
const PERIODICITY_INFO = {
  YEAR: { level: 1, label: 'Year' },
  QTR: { level: 2, label: 'Quarter' },
  MON: { level: 3, label: 'Month' },
};

// All leaf ids under a tree node (the node itself if it has no children).
const leavesOf = (node) => (node.children?.length ? node.children.flatMap(leavesOf) : [node.id]);

// Prune a tree to only the leaves in `keepLeafIds`, retaining ancestors of surviving leaves.
// Returns null when nothing under the node survives.
const pruneTree = (node, keepLeafIds) => {
  if (!node.children?.length) {
    return keepLeafIds.has(node.id) ? node : null;
  }
  const children = node.children.map((child) => pruneTree(child, keepLeafIds)).filter(Boolean);
  return children.length ? { ...node, children } : null;
};

// Smallest unused G<n> key within a dim's groups. Keys are never surfaced to the user or
// developer — they only need to be stable and unique within the dim.
const nextGroupKey = (groups) => {
  const used = new Set(groups.map((group) => group.key));
  let i = 0;
  while (used.has('G' + i)) i++;
  return 'G' + i;
};

// Form field keys for a trended dim's validation. Single-sourced so the registrations (in
// DimConfigPanel / GroupRow) and the per-dim error rollup (dimHasRevealedError) never drift.
const baselineFieldKey = (dimCode) => 'baseline:' + dimCode;
const groupLabelFieldKey = (dimCode, groupKey) => 'group-label:' + dimCode + ':' + groupKey;
const groupMembersFieldKey = (dimCode, groupKey) => 'group-members:' + dimCode + ':' + groupKey;

// Whether any of a trended dim's config fields is currently showing an error (touched + invalid).
// Reads the Form's error/touched maps so a dim's card can flag trouble even while another dim's
// panel is the one on screen.
const dimHasRevealedError = (dimCode, errorStates, touchedStates) => {
  const baseline = baselineFieldKey(dimCode);
  const labelPrefix = groupLabelFieldKey(dimCode, '');
  const membersPrefix = groupMembersFieldKey(dimCode, '');
  return Object.keys(errorStates).some((key) => {
    const belongs = key === baseline || key.startsWith(labelPrefix) || key.startsWith(membersPrefix);
    return belongs && touchedStates[key] && Boolean(errorStates[key]);
  });
};

// A dialog section card: a recessed surface with a small header and a scrollable content area.
// `fill` makes it stretch to its flex parent's height (content scrolls); otherwise it sizes to
// content. Lighter-weight than KrCard, with a smaller title to suit the dense dialog.
const Section = ({ title, fill, children }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      flex: fill ? 1 : '0 0 auto',
      minHeight: 0,
      minWidth: 0,
      bgcolor: 'surface.lowest',
      borderRadius: 1,
      overflow: 'hidden',
    }}
  >
    {title && (
      <Typography variant="subtitle2" sx={{ color: 'text.secondary', px: 2, pt: 1.5, pb: 1 }}>
        {title}
      </Typography>
    )}
    <Box sx={{ flex: fill ? 1 : '0 0 auto', minHeight: 0, overflowY: 'auto', p: 2, pt: title ? 0 : 2 }}>
      {children}
    </Box>
  </Box>
);

// ----- Dimensions section: the Available / Trended drag-and-drop lists -----

const DimensionsSection = ({ codeToDim, availableDimCodes, trendedDimCodes, selectedDimCode, onDragEnd, onSelect }) => {
  const theme = useTheme();
  const { errorStates, touchedStates } = useForm();

  // A draggable dim card. Trended cards are clickable to open their detail panel and highlight when
  // selected, and flag an error state when any of their config fields is touched-and-invalid (so a
  // misconfigured dim is visible even while another dim's panel is on screen).
  const renderDimCard = (dimCode, index, listId) => {
    const isTrended = listId === 'trended';
    const isSelected = isTrended && dimCode === selectedDimCode;
    const hasError = isTrended && dimHasRevealedError(dimCode, errorStates, touchedStates);
    return (
      <Draggable key={dimCode} draggableId={dimCode} index={index}>
        {(prov, snapshot) => (
          <ListItem
            ref={prov.innerRef}
            {...prov.draggableProps}
            {...prov.dragHandleProps}
            onClick={isTrended ? () => onSelect(dimCode) : undefined}
            sx={{
              mb: 1,
              borderRadius: 0.5,
              border: 1,
              borderColor:
                hasError ? theme.palette.error.main
                : isSelected ? theme.palette.primary.main
                : 'transparent',
              bgcolor:
                snapshot.isDragging ? alpha(theme.palette.surface.tint, 0.16)
                : isSelected ? alpha(theme.palette.primary.main, 0.12)
                : hasError ? alpha(theme.palette.error.main, 0.1)
                : theme.palette.surface.container,
              boxShadow: snapshot.isDragging ? 3 : 0,
              transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
              '&:hover': {
                cursor: isTrended ? 'pointer' : 'grab',
                bgcolor: hasError ? alpha(theme.palette.error.main, 0.16) : alpha(theme.palette.surface.tint, 0.08),
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32 }}>
              <FontAwesomeIcon style={{ color: theme.palette.text.secondary }} icon={fas.faGripVertical} />
            </ListItemIcon>
            <ListItemText
              primary={codeToDim[dimCode].displayName}
              sx={{ '& .MuiListItemText-primary': { color: hasError ? 'error.main' : undefined } }}
            />
            {hasError && (
              <FontAwesomeIcon
                style={{ color: theme.palette.error.main, marginLeft: 8 }}
                icon={fas.faExclamationTriangle}
              />
            )}
          </ListItem>
        )}
      </Draggable>
    );
  };

  const renderDropColumn = (id, title, dimCodes, emptyHint) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 0, minHeight: 0 }}>
      <Typography variant="caption" align="center" sx={{ color: 'text.secondary', mb: 0.5 }}>
        {title}
      </Typography>
      <Droppable droppableId={id}>
        {(provided, snapshot) => (
          <List
            ref={provided.innerRef}
            {...provided.droppableProps}
            sx={{
              bgcolor: theme.palette.surface.low,
              flex: 1,
              minHeight: 0,
              overflowY: 'auto',
              borderRadius: 1,
              p: 1,
              border: 1,
              borderColor: snapshot.isDraggingOver ? alpha(theme.palette.surface.tint, 0.16) : 'transparent',
              transition: 'border-color 0.2s ease',
            }}
          >
            {dimCodes.length === 0 && (
              <Typography variant="body2" sx={{ color: 'text.disabled', textAlign: 'center', mt: 1 }}>
                {emptyHint}
              </Typography>
            )}
            {dimCodes.map((dimCode, index) => renderDimCard(dimCode, index, id))}
            {provided.placeholder}
          </List>
        )}
      </Droppable>
    </Box>
  );

  return (
    <Section title="Dimensions" fill>
      <DragDropContext onDragEnd={onDragEnd}>
        <Box sx={{ display: 'flex', gap: 2, height: '100%', minHeight: 0 }}>
          {renderDropColumn('available', 'Available', availableDimCodes, 'All trended')}
          {renderDropColumn('trended', 'Trended', trendedDimCodes, 'Drag dimensions here')}
        </Box>
      </DragDropContext>
    </Section>
  );
};

// ----- Dim config: the per-dim baseline + grouping detail (one panel per trended dim) -----

// One value group's row: a label field plus a member picker, each with its own validation (label
// required and unique within the dim; at least one member). Extracted so every group owns its form
// fields. Validation-only — the group's value lives in the parent's dimGroups map.
const GroupRow = ({ dimCode, group, groups, membersTree, setGroupLabel, setGroupIds, removeGroup }) => {
  const otherGroupIds = groups.filter((other) => other.key !== group.key).flatMap((other) => other.ids);

  const labelField = useFormField(groupLabelFieldKey(dimCode, group.key), [], () => {
    const label = group.label.trim();
    if (!label) return 'Label required';
    const sameLabel = groups.filter((other) => other.label.trim().toLowerCase() === label.toLowerCase());
    return sameLabel.length > 1 ? 'Duplicate label' : null;
  });

  const membersField = useFormField(groupMembersFieldKey(dimCode, group.key), [], () =>
    group.ids.length === 0 ? 'Add at least one member' : null,
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, borderRadius: 1, bgcolor: 'surface.low' }}>
      <TextField
        label="Group Label"
        value={group.label}
        onChange={(event) => setGroupLabel(dimCode, group.key, event.target.value)}
        onBlur={labelField.onBlur}
        error={labelField.error}
        helperText={labelField.helperText}
        sx={{ width: 160 }}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <DimTree
          displayName="Members"
          variant="filled"
          treeData={membersTree}
          selectedIds={group.ids}
          onFilterChange={(ids) => setGroupIds(dimCode, group.key, ids)}
          disabledIds={otherGroupIds}
          error={membersField.error}
          helperText={membersField.helperText}
          onBlur={membersField.onBlur}
        />
      </Box>
      <IconButton size="small" onClick={() => removeGroup(dimCode, group.key)}>
        <FontAwesomeIcon icon={fas.faTrash} />
      </IconButton>
    </Box>
  );
};

// A single trended dim's configuration: baseline values and the grouping mode toggle stay pinned at
// the top; only the groups list (and Add Group) scrolls. Rendered for every trended dim (hidden
// unless selected) so its validation stays mounted and persists across dim switches.
const DimConfigPanel = ({
  dim,
  hidden,
  groups,
  mode,
  baselineIds,
  groupByOptions,
  setBaselineIds,
  addGroup,
  removeGroup,
  setGroupLabel,
  setGroupIds,
  setGroupByMode,
}) => {
  const options = groupByOptions(dim);
  const baselineSet = new Set(baselineIds(dim.code));
  const baselineTree = pruneTree(dim.tree, baselineSet);

  const baselineField = useFormField(baselineFieldKey(dim.code), [], () =>
    baselineIds(dim.code).length === 0 ? 'Select at least one baseline value' : null,
  );

  return (
    <Box
      sx={{ display: hidden ? 'none' : 'flex', flexDirection: 'column', gap: 2, height: '100%', minHeight: 0 }}
    >
      <DimTree
        displayName="Baseline Values"
        variant="filled"
        treeData={dim.tree}
        selectedIds={baselineIds(dim.code)}
        onFilterChange={(ids) => setBaselineIds(dim.code, ids)}
        error={baselineField.error}
        helperText={baselineField.helperText}
        onBlur={baselineField.onBlur}
      />
      {options.length > 0 && (
        <Box>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={mode}
            onChange={(event, value) => value && setGroupByMode(dim.code, value)}
          >
            <ToggleButton value="CUSTOM">Custom</ToggleButton>
            {options.map((periodicity) => (
              <ToggleButton key={periodicity} value={periodicity}>
                {'By ' + PERIODICITY_INFO[periodicity].label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
      )}
      {mode === 'CUSTOM' ?
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {groups.map((group) => (
              <GroupRow
                key={group.key}
                dimCode={dim.code}
                group={group}
                groups={groups}
                membersTree={baselineTree ?? { ...dim.tree, children: [] }}
                setGroupLabel={setGroupLabel}
                setGroupIds={setGroupIds}
                removeGroup={removeGroup}
              />
            ))}
            <Button
              size="small"
              startIcon={<FontAwesomeIcon icon={fas.faPlus} />}
              onClick={() => addGroup(dim.code)}
              sx={{ alignSelf: 'flex-start' }}
            >
              Add Group
            </Button>
          </Box>
        </Box>
      : <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {'Values grouped by ' + PERIODICITY_INFO[mode].label.toLowerCase() + ' on apply.'}
        </Typography>
      }
    </Box>
  );
};

const DimConfigSection = ({
  codeToDim,
  trendedDimCodes,
  selectedDimCode,
  dimGroups,
  dimGroupBy,
  baselineIds,
  groupByOptions,
  setBaselineIds,
  addGroup,
  removeGroup,
  setGroupLabel,
  setGroupIds,
  setGroupByMode,
}) => {
  const selectedDim =
    selectedDimCode && trendedDimCodes.includes(selectedDimCode) ? codeToDim[selectedDimCode] : null;

  // At least one trended dim is required. Validation-only; the value lives in trendedDimCodes.
  const trendedDimsField = useFormField('trended-dims', [], () =>
    trendedDimCodes.length === 0 ? 'Add at least one trended dimension' : null,
  );

  return (
    <Section title={selectedDim ? 'Configure: ' + selectedDim.displayName : 'Configure'} fill>
      {trendedDimCodes.length === 0 ?
        <Typography variant="body2" sx={{ color: trendedDimsField.error ? 'error.main' : 'text.disabled' }}>
          {trendedDimsField.error ? trendedDimsField.helperText : 'Drag a dimension into Trended to begin.'}
        </Typography>
      : <>
          {!selectedDim && (
            <Typography variant="body2" sx={{ color: 'text.disabled' }}>
              Select a trended dimension to configure its values and groups.
            </Typography>
          )}
          {/* All trended dims' panels stay mounted (hidden unless selected) so their state and
              validation persist across selection changes. */}
          {trendedDimCodes.map((dimCode) => (
            <DimConfigPanel
              key={dimCode}
              dim={codeToDim[dimCode]}
              hidden={dimCode !== selectedDimCode}
              groups={dimGroups[dimCode] ?? []}
              mode={dimGroupBy[dimCode] ?? 'CUSTOM'}
              baselineIds={baselineIds}
              groupByOptions={groupByOptions}
              setBaselineIds={setBaselineIds}
              addGroup={addGroup}
              removeGroup={removeGroup}
              setGroupLabel={setGroupLabel}
              setGroupIds={setGroupIds}
              setGroupByMode={setGroupByMode}
            />
          ))}
        </>
      }
    </Section>
  );
};

// ----- Trend output: visible/trended KPIs, layout, totals -----

const TrendOutputSection = ({ viewKpis, trendableKpis, workingActiveKpis, workingTrend, setVisibleKpis, updateTrend }) => {
  // At least one visible KPI (else the grid shows nothing) and one trended KPI are required.
  const visibleKpisField = useFormField('visible-kpis', [], () =>
    workingActiveKpis.length === 0 ? 'Select at least one visible KPI' : null,
  );
  const trendedKpisField = useFormField('trended-kpis', [], () =>
    workingTrend.kpis.length === 0 ? 'Select at least one trended KPI' : null,
  );

  return (
    <Section>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <Box sx={{ flex: '1 1 0', minWidth: 200 }}>
          <KpiSelect
            displayName="Visible KPIs"
            variant="filled"
            kpis={viewKpis}
            selectedCodes={workingActiveKpis}
            onChange={setVisibleKpis}
            error={visibleKpisField.error}
            helperText={visibleKpisField.helperText}
            onBlur={visibleKpisField.onBlur}
          />
        </Box>
        <Box sx={{ flex: '1 1 0', minWidth: 200 }}>
          <KpiSelect
            displayName="Trended KPIs"
            variant="filled"
            kpis={trendableKpis}
            selectedCodes={workingTrend.kpis}
            onChange={(codes) => updateTrend({ kpis: codes })}
            error={trendedKpisField.error}
            helperText={trendedKpisField.helperText}
            onBlur={trendedKpisField.onBlur}
          />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Layout
          </Typography>
          <ToggleButtonGroup
            exclusive
            size="small"
            value={workingTrend.layout}
            onChange={(event, layout) => layout && updateTrend({ layout })}
          >
            {LAYOUTS.map((layout) => (
              <ToggleButton key={layout.value} value={layout.value}>
                {layout.label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={workingTrend.total !== false}
              onChange={(event) => updateTrend({ total: event.target.checked })}
            />
          }
          label="Include Total Column"
        />
      </Box>
    </Section>
  );
};

// Bottom-of-dialog guard: a trend's column count multiplies across trended dims, so a wide baseline
// or many KPIs can explode the grid. Apply is blocked past the limit; the warning message is shown
// after the user attempts Apply (via touchAll), then it tracks the count live as changes are made.
const ColumnLimitMessage = ({ columns, limit, culprit }) => {
  const over = columns > limit;
  const field = useFormField('column-limit', [], () => (over ? 'Too many trend columns' : null));

  // Surfaces only once apply has touched it (apply is this field's "touch"); thereafter it tracks
  // the count live as the user makes fixing changes. Blocking via hasErrorState is touch-independent.
  if (!field.error) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.main' }}>
      <FontAwesomeIcon icon={fas.faExclamationTriangle} />
      <Typography variant="body2">
        {'This trend would render ' + columns + ' columns, over the limit of ' + limit + '.'}
        {culprit &&
          ' ' + culprit.name + ' contributes the most (' + culprit.count + ' values) — group values or narrow its baseline.'}
      </Typography>
    </Box>
  );
};

/**
 * Trend Dimensions dialog. Lets the user override the view's default trend.
 *
 * Layout:
 *  - "Dimensions": two drag-and-drop lists (Available / Trended). Trended order is the nesting
 *    order (top = outer). Selecting a trended dim opens its detail panel.
 *  - "Configure: <Dim>": detail panel for the selected trended dim — baseline values, value groups,
 *    and (for period dims) the group-by-periodicity shortcut.
 *  - "Trend Output": the trend-wide settings — Visible KPIs, Trended KPIs (a subset of visible),
 *    layout, and the total column toggle.
 *
 * On submit the dialog assembles `{ trends, trendValueGroups, trendValueGroupBy }` plus the visible
 * KPI selection, folding each trended dim's baseline values and groups into the trend's `values`.
 */
export const TrendDimsDialog = ({ codeToDim, codeToKpi, kpiCodes, trendSettings, activeKpis, closeDialog }) => {
  // KPIs selectable for a trend are restricted to the active view's KPIs, in view order.
  const viewKpis = kpiCodes.map((code) => codeToKpi[code]);

  // Ref to the Form, used on apply to reveal errors (touchAll) and gate submission (hasErrorState).
  const formRef = useRef();

  // Reconstruct per-dim config (baseline, groups, group-by) for the initial formValues. Prefers a
  // prior session's trendDimConfig (retains config for dims not currently trended); otherwise it
  // rebuilds from the dev-config trend shape (trends + trendValueGroups).
  const seedDimConfig = () => {
    const config = trendSettings?.trendDimConfig;
    if (config) {
      const groups = {};
      const baseline = {};
      const groupBy = {};
      Object.entries(config).forEach(([dimCode, entry]) => {
        if (entry.groups) groups[dimCode] = entry.groups;
        if (entry.baselineIds) baseline[dimCode] = entry.baselineIds;
        if (entry.groupBy) groupBy[dimCode] = entry.groupBy;
      });
      return { groups, baseline, groupBy };
    }

    // Legacy reconstruction: baseline from each trend's `values` (group buckets contribute their
    // member ids, raw buckets their parsed id); groups/groupBy from the dev-config fields.
    const groups = trendSettings?.trendValueGroups ?? {};
    const groupBy = trendSettings?.trendValueGroupBy ?? {};
    const groupKeyToIds = {};
    Object.entries(groups).forEach(([dimCode, gs]) => {
      groupKeyToIds[dimCode] = new Map(gs.map((group) => [StringUtils.toUpperCamel(group.key), group.ids]));
    });
    const baseline = {};
    (trendSettings?.trends ?? []).forEach((trend) => {
      Object.entries(trend.values ?? {}).forEach(([dimCode, buckets]) => {
        if (baseline[dimCode]) return; // dim-scoped: first trend referencing the dim wins
        const byKey = groupKeyToIds[dimCode];
        baseline[dimCode] = buckets.flatMap((bucket) =>
          byKey?.has(bucket.key) ? byKey.get(bucket.key) : [Number(bucket.key)],
        );
      });
    });
    return { groups, baseline, groupBy };
  };

  // All of the dialog's output-shaping state lives in the Form's formValues (the single value store
  // the dialog emits on submit). Purely-UI state (selected dim, Available ordering) stays local.
  const [formValues, setFormValues] = useState(() => {
    const dimConfig = seedDimConfig();
    const fromTrends = (trendSettings?.trends ?? []).flatMap((trend) => trend.dims ?? []);
    const fromGroups = Object.keys(trendSettings?.trendValueGroups ?? {});
    const trend = trendSettings?.trends?.[0];
    return {
      // Which of the view's KPIs are shown in the grid (view-level display filter, not trend-scoped).
      workingActiveKpis: activeKpis ?? kpiCodes,
      // Trended dims, ordered (top = outer). Seeded from the existing trend's dims, then any dim that
      // already has groups defined (so dim-scoped config is preserved even if not yet referenced).
      trendedDimCodes: [...new Set([...fromTrends, ...fromGroups])],
      // Per-dim value groups: { [dimCode]: [{ key, label, ids }] }.
      dimGroups: dimConfig.groups,
      // Per-dim baseline value selection: { [dimCode]: [ids] }. Absent → all discovered (see baselineIds).
      dimBaselineIds: dimConfig.baseline,
      // Per-dim "group by periodicity" mode: { [dimCode]: 'QTR' | 'YEAR' }.
      dimGroupBy: dimConfig.groupBy,
      // The trend's output settings (dims come from trendedDimCodes).
      workingTrend: { kpis: trend?.kpis ?? [], layout: trend?.layout ?? 'KPI_OVER_DIM', total: trend?.total ?? true },
    };
  });

  // Read the output state with the same names the rest of the component already uses.
  const { workingActiveKpis, trendedDimCodes, dimGroups, dimBaselineIds, dimGroupBy, workingTrend } = formValues;

  // Update a single formValues key (accepts a value or an updater function).
  const setField = (key, value) =>
    setFormValues((prev) => ({ ...prev, [key]: typeof value === 'function' ? value(prev[key]) : value }));

  // Setter shims so existing callers keep their names and value/updater semantics.
  const setWorkingActiveKpis = (value) => setField('workingActiveKpis', value);
  const setTrendedDimCodes = (value) => setField('trendedDimCodes', value);
  const setDimGroups = (value) => setField('dimGroups', value);
  const setDimBaselineIds = (value) => setField('dimBaselineIds', value);
  const setDimGroupBy = (value) => setField('dimGroupBy', value);
  const setWorkingTrend = (value) => setField('workingTrend', value);

  // Untrended dims, ordered (purely-UI; preserved at the drop position). Every dim is in one list.
  const [availableDimCodes, setAvailableDimCodes] = useState(() =>
    Object.keys(codeToDim).filter((code) => !trendedDimCodes.includes(code)),
  );

  // The trended dim selected for the detail panel.
  const [selectedDimCode, setSelectedDimCode] = useState(() => trendedDimCodes[0] ?? null);

  // Effective baseline ids for a dim: an explicit selection (config-derived or user-edited) wins;
  // otherwise default to every value in the dim so the baseline starts fully selected.
  const baselineIds = (dimCode) => dimBaselineIds[dimCode] ?? TreeUtils.parseTree(codeToDim[dimCode].tree).leafIds;

  // Visible KPIs cascade to trended KPIs: a KPI can only be trended if it is visible, so dropping a
  // KPI from the visible set removes it from the trend.
  const setVisibleKpis = (codes) => {
    setWorkingActiveKpis(codes);
    const visible = new Set(codes);
    setWorkingTrend((trend) => ({ ...trend, kpis: trend.kpis.filter((code) => visible.has(code)) }));
  };

  // Drag between / within the Available and Trended lists. Both lists are ordered: Trended order is
  // the nesting order; Available order is purely cosmetic but preserved at the drop position.
  const handleDragEnd = ({ source, destination, draggableId }) => {
    if (!destination) return;
    const from = source.droppableId;
    const to = destination.droppableId;

    // Reorder within a single list.
    if (from === to) {
      const list = from === 'trended' ? trendedDimCodes : availableDimCodes;
      const setList = from === 'trended' ? setTrendedDimCodes : setAvailableDimCodes;
      const next = [...list];
      next.splice(source.index, 1);
      next.splice(destination.index, 0, draggableId);
      setList(next);
      return;
    }

    // Move across lists, inserting at the dropped position.
    if (to === 'trended') {
      setAvailableDimCodes((prev) => prev.filter((code) => code !== draggableId));
      const next = [...trendedDimCodes];
      next.splice(destination.index, 0, draggableId);
      setTrendedDimCodes(next);
      setSelectedDimCode(draggableId); // auto-select on drop
    } else {
      const nextTrended = trendedDimCodes.filter((code) => code !== draggableId);
      setTrendedDimCodes(nextTrended);
      const nextAvailable = [...availableDimCodes];
      nextAvailable.splice(destination.index, 0, draggableId);
      setAvailableDimCodes(nextAvailable);
      // Keep a selection if any trended dims remain — fall back to the last one.
      setSelectedDimCode((prev) => (prev === draggableId ? (nextTrended[nextTrended.length - 1] ?? null) : prev));
    }
  };

  const setBaselineIds = (dimCode, ids) => {
    setDimBaselineIds((prev) => ({ ...prev, [dimCode]: ids }));
    // Groups are a subset of the baseline: dropping a value from the baseline removes it from any
    // group that held it.
    const baselineSet = new Set(ids);
    updateDimGroups(dimCode, (groups) =>
      groups.map((group) => ({ ...group, ids: group.ids.filter((id) => baselineSet.has(id)) })),
    );
  };

  // ----- Group editing -----

  const updateDimGroups = (dimCode, updater) => {
    setDimGroups((prev) => ({ ...prev, [dimCode]: updater(prev[dimCode] ?? []) }));
  };

  const addGroup = (dimCode) => {
    updateDimGroups(dimCode, (groups) => [...groups, { key: nextGroupKey(groups), label: '', ids: [] }]);
  };

  const removeGroup = (dimCode, key) => {
    updateDimGroups(dimCode, (groups) => groups.filter((group) => group.key !== key));
  };

  const setGroupLabel = (dimCode, key, label) => {
    updateDimGroups(dimCode, (groups) => groups.map((group) => (group.key === key ? { ...group, label } : group)));
  };

  const setGroupIds = (dimCode, key, ids) => {
    updateDimGroups(dimCode, (groups) => groups.map((group) => (group.key === key ? { ...group, ids } : group)));
  };

  // ----- Group by periodicity -----

  // Periodicities a period dim can be grouped by: those that appear as grouping (internal) nodes in
  // the dim's tree — i.e. above the leaf level — finest-first (e.g. Quarter before Year). The period
  // tree always builds Year ▸ Quarter ▸ Month grouping nodes above its leaves, so this reflects the
  // real available levels regardless of the dim's storage periodicity. Empty for non-period dims.
  const groupByOptions = (dim) => {
    if (!dim.periodicities?.length) return [];
    const groupingLevels = new Set();
    const walk = (node) => {
      if (node.children?.length) {
        groupingLevels.add(node.level);
        node.children.forEach(walk);
      }
    };
    walk(dim.tree);
    return Object.entries(PERIODICITY_INFO)
      .filter(([, info]) => groupingLevels.has(info.level))
      .sort((a, b) => b[1].level - a[1].level)
      .map(([periodicity]) => periodicity);
  };

  const setGroupByMode = (dimCode, mode) => {
    setDimGroupBy((prev) => {
      const next = { ...prev };
      if (mode === 'CUSTOM') delete next[dimCode];
      else next[dimCode] = mode;
      return next;
    });
  };

  // Materialize { key, label, ids } groups for a period dim from its baseline: one group per tree
  // node at the periodicity's level, holding that node's baseline-selected leaves. Empty buckets
  // (no selected leaves) are skipped, so labels stay honest.
  const periodGroups = (dimCode, periodicity) => {
    const level = PERIODICITY_INFO[periodicity].level;
    const baselineSet = new Set(baselineIds(dimCode));
    const groups = [];
    const walk = (node) => {
      if (node.level === level) {
        const ids = leavesOf(node).filter((id) => baselineSet.has(id));
        if (ids.length > 0) groups.push({ key: 'G' + groups.length, label: node.name, ids });
        return;
      }
      (node.children ?? []).forEach(walk);
    };
    walk(codeToDim[dimCode].tree);
    return groups;
  };

  // ----- Trend editing -----

  const updateTrend = (patch) => {
    setWorkingTrend((trend) => ({ ...trend, ...patch }));
  };

  // Number of value buckets a trended dim contributes (mirrors dimValues in buildTrendSettings):
  // its period buckets in group-by mode, otherwise its valid groups plus the ungrouped baseline.
  const dimBucketCount = (dimCode) => {
    const groupBy = dimGroupBy[dimCode];
    if (groupBy) return periodGroups(dimCode, groupBy).length;
    const groups = (dimGroups[dimCode] ?? []).filter((group) => group.ids.length > 0 && group.label.trim());
    const groupedIds = new Set(groups.flatMap((group) => group.ids));
    const ungrouped = baselineIds(dimCode).filter((id) => !groupedIds.has(id)).length;
    return groups.length + ungrouped;
  };

  // Projected grid column count and the trended dim contributing the most buckets (the biggest
  // lever, since the trended dims multiply). Mirrors EntityPerspective's buildTrendColumns: each
  // trended KPI expands across the product of the dims' buckets, with a subtotal leaf per inner
  // level and a trailing grand-total leaf when totals are on; each non-trended visible KPI is one
  // plain column. Used purely to warn — buildTrendSettings remains the source of truth.
  const trendColumnInfo = () => {
    const trendedKpiCount = workingTrend.kpis.length;
    const nonTrendedVisible = workingActiveKpis.filter((code) => !workingTrend.kpis.includes(code)).length;
    if (trendedKpiCount === 0 || trendedDimCodes.length === 0) {
      return { columns: nonTrendedVisible, culprit: null };
    }

    const totalOn =
      workingTrend.layout === 'KPI_OVER_DIM' ? workingTrend.total !== false : workingTrend.total === true;
    const buckets = trendedDimCodes.map(dimBucketCount);

    // makeLeaf calls produced by buildTrendColumns: at each non-leaf level a subtotal adds one.
    const leavesFrom = (depth) =>
      depth === buckets.length - 1 ?
        buckets[depth]
      : buckets[depth] * (leavesFrom(depth + 1) + (totalOn ? 1 : 0));
    const perKpi = leavesFrom(0) + (totalOn ? 1 : 0);
    const columns = trendedKpiCount * perKpi + nonTrendedVisible;

    const culpritCode = trendedDimCodes.reduce((max, code) =>
      dimBucketCount(code) > dimBucketCount(max) ? code : max,
    );
    const culprit = { name: codeToDim[culpritCode].displayName, count: dimBucketCount(culpritCode) };

    return { columns, culprit };
  };

  // Assemble the activeTrendSettings output: trendValueGroups (per trended dim) plus, for each
  // trend, an explicit per-dim `values` folding the dim's groups and ungrouped baseline values
  // into the ordered { key, label } bucket list dimBuckets consumes (groups first, then raw).
  const buildTrendSettings = () => {
    // Per trended dim: either materialize period groups (group-by mode) or use the manual groups
    // (dropping empty ones). trendValueGroupBy records the mode so the dialog can restore it; the
    // backend / dimBuckets only consume the resulting trendValueGroups.
    const trendValueGroups = {};
    const trendValueGroupBy = {};
    trendedDimCodes.forEach((dimCode) => {
      const groupBy = dimGroupBy[dimCode];
      if (groupBy) {
        trendValueGroupBy[dimCode] = groupBy;
        const groups = periodGroups(dimCode, groupBy);
        if (groups.length > 0) trendValueGroups[dimCode] = groups;
      } else {
        const groups = (dimGroups[dimCode] ?? []).filter((group) => group.ids.length > 0 && group.label.trim());
        if (groups.length > 0) trendValueGroups[dimCode] = groups;
      }
    });

    // Ordered bucket descriptors ({ key, label }) for one dim: its groups, then ungrouped baseline.
    const dimValues = (dimCode) => {
      const groups = trendValueGroups[dimCode] ?? [];
      const groupedIds = new Set(groups.flatMap((group) => group.ids));
      const idToNode = TreeUtils.parseTree(codeToDim[dimCode].tree).idToNode;

      const groupBuckets = groups.map((group) => ({
        key: StringUtils.toUpperCamel(group.key),
        label: group.label,
      }));
      const rawBuckets = baselineIds(dimCode)
        .filter((id) => !groupedIds.has(id))
        .map((id) => ({ key: String(id), label: idToNode[id]?.name ?? String(id) }));

      return [...groupBuckets, ...rawBuckets];
    };

    const trend = { ...workingTrend, dims: trendedDimCodes };
    const trends =
      trend.dims.length > 0 && trend.kpis.length > 0 ?
        [
          {
            ...trend,
            values: Object.fromEntries(trend.dims.map((dimCode) => [dimCode, dimValues(dimCode)])),
          },
        ]
      : [];

    // Persist per-dim working config (baseline, groups, group-by) for every configured dim, even
    // ones not currently trended, so re-adding a dim or reopening the dialog restores its settings.
    // Dialog-only — the backend and dimBuckets never read this.
    const trendDimConfig = {};
    const configDimCodes = new Set([
      ...Object.keys(dimBaselineIds),
      ...Object.keys(dimGroups),
      ...Object.keys(dimGroupBy),
    ]);
    configDimCodes.forEach((dimCode) => {
      const entry = {};
      if (dimBaselineIds[dimCode]) entry.baselineIds = dimBaselineIds[dimCode];
      if (dimGroups[dimCode]?.length) entry.groups = dimGroups[dimCode];
      if (dimGroupBy[dimCode]) entry.groupBy = dimGroupBy[dimCode];
      if (Object.keys(entry).length > 0) trendDimConfig[dimCode] = entry;
    });

    return { trends, trendValueGroups, trendValueGroupBy, trendDimConfig };
  };

  // Whether a trended dim currently has a blocking validation error. Navigation-only: mirrors the
  // per-field validations in DimConfigPanel / GroupRow so a blocked apply can reveal the offending
  // (possibly hidden) panel. The form fields remain the source of truth for blocking.
  const dimHasError = (dimCode) => {
    if (baselineIds(dimCode).length === 0) return true;
    if (dimGroupBy[dimCode]) return false; // group-by mode materializes groups; only baseline applies
    const groups = dimGroups[dimCode] ?? [];
    const labels = groups.map((group) => group.label.trim().toLowerCase());
    return groups.some(
      (group, index) => !group.label.trim() || group.ids.length === 0 || labels.indexOf(labels[index]) !== index,
    );
  };

  const submit = () => {
    // Reveal any pending validation errors; block the apply while the form is invalid.
    formRef.current.touchAll();
    if (formRef.current.hasErrorState()) {
      // Surface the first invalid trended dim so its (possibly hidden) panel shows why apply blocked.
      const invalidDim = trendedDimCodes.find(dimHasError);
      if (invalidDim) setSelectedDimCode(invalidDim);
      return;
    }
    const trendSettings = buildTrendSettings();
    closeDialog({ trendSettings, activeKpis: workingActiveKpis });
  };

  const trendableKpis = viewKpis.filter((kpi) => workingActiveKpis.includes(kpi.code));
  const columnInfo = trendColumnInfo();

  return (
    <Dialog onClose={() => closeDialog()} open fullWidth maxWidth="lg">
      <Form ref={formRef} formValues={formValues} setFormValues={setFormValues}>
        <DialogTitle>Trend Dimensions</DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, padding: 2, height: '70vh', minHeight: 0 }}>
            <Box sx={{ display: 'flex', gap: 2, flex: 1, minHeight: 0 }}>
              <Box sx={{ flex: '2 1 0', minWidth: 0, minHeight: 0, display: 'flex' }}>
                <DimensionsSection
                  codeToDim={codeToDim}
                  availableDimCodes={availableDimCodes}
                  trendedDimCodes={trendedDimCodes}
                  selectedDimCode={selectedDimCode}
                  onDragEnd={handleDragEnd}
                  onSelect={setSelectedDimCode}
                />
              </Box>
              <Box sx={{ flex: '3 1 0', minWidth: 0, minHeight: 0, display: 'flex' }}>
                <DimConfigSection
                  codeToDim={codeToDim}
                  trendedDimCodes={trendedDimCodes}
                  selectedDimCode={selectedDimCode}
                  dimGroups={dimGroups}
                  dimGroupBy={dimGroupBy}
                  baselineIds={baselineIds}
                  groupByOptions={groupByOptions}
                  setBaselineIds={setBaselineIds}
                  addGroup={addGroup}
                  removeGroup={removeGroup}
                  setGroupLabel={setGroupLabel}
                  setGroupIds={setGroupIds}
                  setGroupByMode={setGroupByMode}
                />
              </Box>
            </Box>

            <TrendOutputSection
              viewKpis={viewKpis}
              trendableKpis={trendableKpis}
              workingActiveKpis={workingActiveKpis}
              workingTrend={workingTrend}
              setVisibleKpis={setVisibleKpis}
              updateTrend={updateTrend}
            />

            <ColumnLimitMessage columns={columnInfo.columns} limit={COLUMN_LIMIT} culprit={columnInfo.culprit} />
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={() => closeDialog()}>
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={submit}>
            Apply
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};
